import { helpWidgetLoadFailMessage } from './loadMiniMavenHelpWidget.locstring.json';
import loc from 'owa-localize';
import { action } from 'satcheljs/lib/legacy';
import { getPaletteAsRawColors } from 'owa-theme';
import { getPackageBaseUrl } from 'owa-config';
import { resolve as urlResolve } from 'url';
import * as HelpCharmConstants from '../utils/HelpCharmConstants';
import OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

import { WidgetParams, ClientContext, MiniMavenTheme } from './../schema/MiniMavenOseFo';
import sendMiniMavenDataPoint from '../services/sendMiniMavenDataPoint';
import { getGuid } from 'owa-guid';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getSupportTicketMetaBlob } from '../utils/getSupportTicketMetaBlob';
import extractAndSendDiagnosticsInfoToODS from '../utils/extractAndSendDiagnosticsInfoToODS';
import {
    isInPremiumSupportTreatmentFlight,
    isInPremiumSupportControlFlight,
} from 'owa-premium-support';

let onPaneDismiss: any;
let sessionId: string;
let widgetInitiationTime: number;
let widgetLoadFailTimeoutId: number;
let widgetPlaceholderDiv: HTMLDivElement;
let scriptElement;
let userConfig: OwaUserConfiguration;
let isWidgetLoaded: boolean;

export default action('loadMiniMavenHelpWidget')(async function loadMiniMavenHelpWidget(
    hostingDivElement: HTMLDivElement,
    onDismiss: any
) {
    if (hostingDivElement) {
        onPaneDismiss = onDismiss;
        widgetPlaceholderDiv = hostingDivElement;
        sessionId = getGuid();
        userConfig = getUserConfiguration();
        scriptElement = document.createElement('script');
        scriptElement.src = urlResolve(getPackageBaseUrl(), HelpCharmConstants.OseFoScriptPath);
        isWidgetLoaded = false;

        let shouldDisableTicket = await shouldDisableTicketFlow();
        let supportTicketMeta = await getSupportTicketMetaBlob();

        scriptElement.onload = function() {
            var clientContext: ClientContext = {
                sessionId: sessionId,
                appId: HelpCharmConstants.WidgetAppId,
                locale: userConfig.SessionSettings.UserCulture,
                hideHeader: true,
                disableTicketFlow: shouldDisableTicket,
                theme: getUserTheme(),
                metadata: supportTicketMeta,
            };

            var widgetParams: WidgetParams = {
                hostingDiv: hostingDivElement,
                baseUrl: HelpCharmConstants.WidgetBaseUrl,
                clientContext: clientContext,
                widgetStyle: HelpCharmConstants.WidgetDefaultStyle,
                widgetEventListenerCallback: widgetEventListner,
            };

            widgetLoadFailTimeoutId = window.setTimeout(
                onWidgetLoadTimeout,
                HelpCharmConstants.MiniMavenWidgetLoadTimeOut
            );

            sendMiniMavenDataPoint({
                sessionId: sessionId,
                eventId: 'LaunchWidget',
                eventData: HelpCharmConstants.OwaMailEventData,
                loadTime: '',
                isDeeplinkScenario: '',
            });

            widgetInitiationTime = new Date().getTime();

            window.Osefo.CreateWidget(widgetParams);
        };

        document.head.appendChild(scriptElement);
    }
});

export function destroyWidgetOnPaneDismiss() {
    if (window.Osefo) {
        window.Osefo.DestroyWidget(window.Osefo.IFrameContainer.WidgetContainer);
    }

    if (scriptElement && document.head.contains(scriptElement)) {
        document.head.removeChild(scriptElement);
    }

    // Capture these events to understand if the widget was loaded or not when user dimissed the help pane
    if (isWidgetLoaded) {
        sendMiniMavenDataPoint({
            sessionId: sessionId,
            eventId: 'DismissedAfterWidgetLoad',
            eventData: HelpCharmConstants.OwaMailEventData,
            loadTime: '',
            isDeeplinkScenario: '',
        });
    } else {
        sendMiniMavenDataPoint({
            sessionId: sessionId,
            eventId: 'DismissedBeforeWidgetLoad',
            eventData: HelpCharmConstants.OwaMailEventData,
            loadTime: '',
            isDeeplinkScenario: '',
        });
    }
}

function getUserTheme(): MiniMavenTheme {
    // This uses `getPaletteAsRawColors()`, rather than `getPalette()`,
    // because the values (which may be CSS variable references) are being passed outside the
    // scope of the OWA client window
    let { themePrimary, themeDark } = getPaletteAsRawColors();

    let userTheme: MiniMavenTheme = {
        button: {
            bgColor: {
                onActive: themePrimary,
                onHover: themeDark,
            },
            borderColor: {
                onActive: themePrimary,
                onHover: themeDark,
            },
        },
        link: {
            color: {
                onActive: themePrimary,
                onHover: themeDark,
            },
        },
    };

    return userTheme;
}

function widgetEventListner(eventName: string) {
    // Widget dismiss event
    if (eventName == HelpCharmConstants.OsefoDismissWidgetEvent) {
        sendMiniMavenDataPoint({
            sessionId: sessionId,
            eventId: 'OnWidgetDismissEvent',
            eventData: HelpCharmConstants.OwaMailEventData,
            loadTime: '',
            isDeeplinkScenario: '',
        });

        onPaneDismiss();
    } else if (eventName == HelpCharmConstants.OsefoLoadWidgetEvent) {
        isWidgetLoaded = true;
        window.clearTimeout(widgetLoadFailTimeoutId);
        sendMiniMavenDataPoint({
            sessionId: sessionId,
            eventId: 'OnWidgetLoadEvent',
            eventData: HelpCharmConstants.OwaMailEventData,
            loadTime: String(new Date().getTime() - widgetInitiationTime),
            isDeeplinkScenario: '',
        });
    } else if (eventName.indexOf(HelpCharmConstants.OsefoTicketCreatedEvent) > -1) {
        extractAndSendDiagnosticsInfoToODS(eventName, sessionId);
    }
}

function onWidgetLoadTimeout() {
    widgetPlaceholderDiv.innerText = loc(helpWidgetLoadFailMessage);
    sendMiniMavenDataPoint({
        sessionId: sessionId,
        eventId: 'OnWidgetLoadFailEvent',
        eventData: HelpCharmConstants.OwaMailEventData,
        loadTime: String(new Date().getTime() - widgetInitiationTime),
        isDeeplinkScenario: '',
    });
}

async function shouldDisableTicketFlow() {
    if (isFeatureEnabled('help-disableTicketFlow')) {
        // When the ticket flow is disabled by help-disableTicketFlow flight
        // The user is in premium support treatment flight, we want user be able to fire ticket
        let inPremiumSupportTreatmentFlight = await isInPremiumSupportTreatmentFlight();
        let inPremiumSupportControlFlight = await isInPremiumSupportControlFlight();
        if (inPremiumSupportTreatmentFlight || inPremiumSupportControlFlight) {
            return false;
        }
        return true;
    } else {
        return false;
    }
}
